import type { CommonProject } from '../universal-nav/types';

/**
 * Find the project in `projects` whose id equals `projectId`.
 * Single source of truth for "which project is active" — used by both the
 * initial sync (`init`) and the `projectId` watcher so highlight + scroll
 * always target the same item.
 */
export function findSelectedProject(
  projects: CommonProject[],
  projectId: string | undefined
): CommonProject | undefined {
  if (!projectId) return undefined;
  return projects.find(({ project: { id } }) => id === projectId);
}

/** Minimal structural type for the SimpleBar-backed `ps-scrollbar`. */
type ScrollbarLike = Element & {
  simplebar?: { getScrollElement(): HTMLElement | null };
};

export interface MegaMenuScrollTarget {
  item: HTMLElement;
  container: HTMLElement;
}

/**
 * Within an open mega-menu popover, locate the selected project menu-item and
 * the scrollable container that needs to scroll it into view. Returns null when
 * either is missing (no active project, or the scrollbar isn't ready yet).
 *
 * Matches on the `selected` attribute because Lit's `?selected=` binding toggles
 * the attribute, which is exactly what is present in the rendered DOM.
 */
export function findMegaMenuScrollTarget(
  root: ParentNode | null | undefined
): MegaMenuScrollTarget | null {
  if (!root) return null;

  const items = Array.from(root.querySelectorAll<HTMLElement>('ps-menu-item'));
  const item = items.find((el) => el.hasAttribute('selected'));
  if (!item) return null;

  const scrollbar = root.querySelector<ScrollbarLike>(
    '.c-mega-menu__scrollbar'
  );
  const container = scrollbar?.simplebar?.getScrollElement() ?? null;
  if (!container) return null;

  return { item, container };
}

export type MegaMenuToolClickAction =
  | 'manager-nav'
  | 'change-org'
  | 'native'
  | 'none';

/**
 * Decide what a mega-menu tool-tile click should do. The tile is always a real `<a href>` now, so
 * we only intercept a PLAIN left click on the Manager (Organization) tile to drive SPA navigation
 * (+ close the menu). Any modified click (cmd/ctrl/shift/alt/middle/right) resolves to 'native' so
 * the anchor opens a new tab/window/context-menu itself (PSTUDIO-3824). Non-Manager tiles keep the
 * prior behavior of resetting org selection on click.
 */
export function resolveMegaMenuToolClick(params: {
  isOrganization: boolean;
  isPlainLeftClick: boolean;
  hasManagerHandler: boolean;
  hasChangeOrgSelection: boolean;
}): MegaMenuToolClickAction {
  const {
    isOrganization,
    isPlainLeftClick,
    hasManagerHandler,
    hasChangeOrgSelection,
  } = params;

  if (isOrganization) {
    return hasManagerHandler && isPlainLeftClick ? 'manager-nav' : 'native';
  }
  return hasChangeOrgSelection ? 'change-org' : 'none';
}
